import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, InputGroup, Modal, Spinner, Overlay } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { toArray } from 'lodash';
import { toggleEditModal, updateUserData } from '../slices/profilePageSlice';
import { AppState } from '../../../store/store';

import { ResponsePrivateProfile } from '../../../services/api/models/response/responsePrivateProfile';
import { ResponsePersonalData } from '../../../services/api/models/response/responsePersonalData';
import {
  validateAge,
  validateEmail,
  validateLoginId,
} from '../../../common/utils/utils';
import { ResponseImages } from '../../../services/api/models/response/responseImages';
import { ImageCustom } from '../../../common/components/ImageCustom';
import copySvg from '../../../assets/svg/copy.svg';
import {
  useGetSelfPrivateProfileMutation,
  useGetUserCodeMutation,
  usePostUserCodeMutation,
  usePostResourceMutation,
  useUpdateSelfAvatarMutation,
  useUpdateSelfLoginIdMutation,
  useUpdateSelfPrivateProfileMutation,
  useUpdateUserInfoMutation,
  useValidateLoginIdMutation,
} from '../../../services/api/apiProvider';
import './EditProfileModal.scss';

const CATCH_PHRASE_MAX_LENGTH = 20;
const DESCRIPTION_MAX_LENGTH = 250;

export const EditProfileModal = (): ReactElement => {
  const dispatch = useDispatch();

  // user public info
  const { userData, isShowEditModal } = useSelector(
    (state: AppState) => state.profilePageState,
  );
  const [loginId, setLoginId] = useState(userData?.user.loginId);
  const [catchPhrase, setCatchPhrase] = useState(
    userData?.user.catchPhrase?.value,
  );
  const [description, setDescription] = useState(
    userData?.user.description?.value,
  );
  const [avatarSrc, setAvatarSrc] = useState(userData?.user.picture.uri);

  // user private info
  const [privateProfile, setPrivateProfile] =
    useState<ResponsePrivateProfile>();
  const [gender, setGender] = useState<string>('male');
  const [age, setAge] = useState<number>();
  const [email, setEmail] = useState<string>();

  // user code
  const [userCode, setUserCode] = useState<string>('');
  const [isShowCopiedNotice, setIsShowCopiedNotice] = useState(false);
  const userCodeRef = useRef(null);
  // loading state
  const [loading, setLoading] = useState(false);
  // API
  const [getPrivateProfileAPI] = useGetSelfPrivateProfileMutation();
  const [getUserCode] = useGetUserCodeMutation();
  const [postUserCode] = usePostUserCodeMutation();
  const [updateUserInfoAPI] = useUpdateUserInfoMutation();
  const [validateLoginIdAPI] = useValidateLoginIdMutation();
  const [updateLoginIdAPI] = useUpdateSelfLoginIdMutation();
  const [updatePrivateProfileAPI] = useUpdateSelfPrivateProfileMutation();
  const [postResourceAPI] = usePostResourceMutation();
  const [updateAvatarAPI] = useUpdateSelfAvatarMutation();

  useEffect(() => {
    fetchPrivateProfile();
    fetchUserCode();
  }, []);

  /**
   * Fetch user private profile data
   */
  const fetchPrivateProfile = async () => {
    const data = await getPrivateProfileAPI().unwrap();
    if (data) {
      setPrivateProfile(data);
      setGender(data.gender);
      setAge(data.age);
      setEmail(data.email);
    }
  };
  /**
   * Fetch user code
   */
  const fetchUserCode = async () => {
    const data = await getUserCode().unwrap();
    if (data) {
      setUserCode(data.code);
    } else {
      const dataPost = await postUserCode().unwrap();
      setUserCode(dataPost.code);
    }
  };
  /**
   * Handle save changes
   */
  const handleSave = async () => {
    if (!userData || !privateProfile) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const newUserData = JSON.parse(
      JSON.stringify(userData),
    ) as ResponsePersonalData;
    let hasError = false;

    // update login ID if has change
    if (userData.user.loginId !== loginId) {
      try {
        await updateLoginId();
        if (loginId) {
          newUserData.user.loginId = loginId;
        }
      } catch (error: any) {
        hasError = true;
        toast.error(error.message || 'Update login ID failed');
      }
    }

    // update user info if has change
    if (
      userData.user.catchPhrase.value !== catchPhrase ||
      userData.user.description.value !== description
    ) {
      try {
        await updateUserInfo();
        newUserData.user.catchPhrase.value = catchPhrase;
        newUserData.user.description.value = description;
      } catch (error) {
        hasError = true;
        toast.error('Update user information failed');
      }
    }

    // update private info if has change
    if (
      privateProfile.gender !== gender ||
      privateProfile.age !== age ||
      privateProfile.email !== email
    ) {
      try {
        await updatePrivateProfile();
      } catch (error) {
        hasError = true;
        toast.error('Update user private information failed');
      }
    }

    // upload avatar if user puts a new image file
    try {
      const imageData = await uploadAvatar();
      if (imageData) {
        newUserData.user.picture = imageData;
      }
    } catch (error) {
      hasError = true;
      toast.error('Update user avatar failed');
    }

    // All update are success
    if (!hasError) {
      toast.success('Update successfully');
    }
    setLoading(false);
    dispatch(updateUserData(newUserData));
    dispatch(toggleEditModal(false));
  };

  /**
   * Validate form
   * @returns all fields is valid?
   */
  const validateForm = (): boolean => {
    // validate login Id
    if (!loginId) {
      toast.error('Login ID is required');
      return false;
    }
    if (!validateLoginId(loginId)) {
      toast.error('Login ID is not valid');
      return false;
    }

    // validate catch phrase
    if (catchPhrase && toArray(catchPhrase).length > CATCH_PHRASE_MAX_LENGTH) {
      toast.error('Catch phrase is not valid');
      return false;
    }

    // validate description
    if (description && toArray(description).length > DESCRIPTION_MAX_LENGTH) {
      toast.error('Description is not valid');
      return false;
    }

    // validate age
    if (age !== undefined && !validateAge(age)) {
      toast.error('Age is not valid');
      return false;
    }

    // validate email
    if (email === '' || (email && !validateEmail(email))) {
      toast.error('Email is not valid');
      return false;
    }

    return true;
  };

  /**
   * Update login ID
   */
  const updateLoginId = async () => {
    if (!loginId) {
      throw new Error('Login ID is required');
    }

    // check login ID is unique
    const isUnique = await validateLoginIdAPI(loginId).unwrap();
    if (!isUnique) {
      throw new Error('Login ID was used. Please provide another one');
    }

    await updateLoginIdAPI(loginId).unwrap();
  };

  /**
   * Update user info: catch phrase, description.
   */
  const updateUserInfo = async () => {
    await updateUserInfoAPI({
      catchPhrase,
      description,
    }).unwrap();
  };

  /**
   * Update private profile: gender, age, email
   */
  const updatePrivateProfile = async () => {
    await updatePrivateProfileAPI({
      gender,
      age,
      email,
    }).unwrap();
  };

  /**
   * Upload avatar source if user puts an image file
   * Then update user avatar when get new resource
   * @returns Image resource data
   */
  const uploadAvatar = async (): Promise<ResponseImages | undefined> => {
    const fileInput = document.getElementById(
      'avatar-input',
    ) as HTMLInputElement;

    if (fileInput) {
      const { files } = fileInput;
      if (files && files.length > 0) {
        const file = files[0];
        const formData = new FormData();
        formData.append('file', file);

        const responseImage = await postResourceAPI(formData).unwrap();
        if (responseImage && responseImage.id) {
          await updateAvatarAPI(responseImage.id).unwrap();
          return responseImage;
        }
      }
    }

    return undefined;
  };

  const handleSelectFile = (event: any) => {
    const { files } = event.target;
    if (FileReader && files && files.length > 0) {
      const fr = new FileReader();
      fr.onload = () => {
        setAvatarSrc(fr.result as string);
      };
      fr.readAsDataURL(files[0]);
    }
  };

  // copy user code when click function
  const copyUserCode = () => {
    const userCodeToCopy = userCode;
    navigator.clipboard.writeText(userCodeToCopy);
    setIsShowCopiedNotice(true);
    setTimeout(() => {
      setIsShowCopiedNotice(false);
    }, 2000);
  };

  return (
    <Modal
      show={isShowEditModal}
      backdrop="static"
      keyboard={false}
      scrollable={true}
      onHide={() => {
        dispatch(toggleEditModal(false));
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>プロフィールを編集</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="profile__avatar mx-auto mb-4">
          <ImageCustom src={avatarSrc} />
          <div className="profile__avatar-overlay">
            <input
              type="file"
              id="avatar-input"
              className="profile__avatar-input"
              onChange={handleSelectFile}
              accept="image/*"
            />
            <label className="profile__avatar-edit-btn" htmlFor="avatar-input">
              編集
            </label>
          </div>
        </div>

        <Form>
          {/* User Code */}
          <div className="mb-3">
            <Form.Label>ユーザーコード</Form.Label>
            <InputGroup>
              <InputGroup.Text
                style={{ cursor: 'pointer', backgroundColor: '#fff' }}
                onClick={copyUserCode}
                className="btn-copyUserCode"
                ref={userCodeRef}
              >
                <img
                  src={copySvg}
                  alt=""
                  style={{
                    width: '15px',
                    marginRight: '5px',
                  }}
                />
                コピー
              </InputGroup.Text>
              <Overlay show={isShowCopiedNotice} target={userCodeRef}>
                <div className="popup">Copied!</div>
              </Overlay>
              <Form.Control
                placeholder="ユーザーコード"
                value={userCode || ''}
                readOnly
                style={{ backgroundColor: '#e9ecef', cursor: 'default' }}
                onClick={copyUserCode}
              />
            </InputGroup>
          </div>

          {/* Login ID */}
          <div className="mb-3">
            <Form.Label>every ID</Form.Label>
            <InputGroup>
              <InputGroup.Text>@</InputGroup.Text>
              <Form.Control
                placeholder="ID変更を入力します"
                value={loginId || ''}
                onChange={(event) => {
                  setLoginId(event.target.value);
                }}
              />
            </InputGroup>
            <Form.Text>※4文字以上14文字以内で設定してください</Form.Text>
          </div>

          {/* Catch phrase */}
          <div className="mb-3">
            <Form.Label>ひとこと</Form.Label>
            <Form.Control
              placeholder="ひとことを入力します"
              value={catchPhrase || ''}
              onChange={(event) => {
                setCatchPhrase(event.target.value);
              }}
            />
            <div className="d-flex">
              <Form.Text>意気込みやライプ告知などを入力できます。</Form.Text>
              <Form.Text className="ml-auto">{`(${
                toArray(catchPhrase || '').length
              }/${CATCH_PHRASE_MAX_LENGTH})`}</Form.Text>
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <Form.Label>紹介文</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              placeholder="紹介文を入力します"
              value={description || ''}
              onChange={(event) => {
                setDescription(event.target.value);
              }}
            />
            <div className="d-flex">
              <Form.Text className="ml-auto">{`(${
                toArray(description || '').length
              }/${DESCRIPTION_MAX_LENGTH})`}</Form.Text>
            </div>
          </div>

          {privateProfile && (
            <>
              {/* Gender */}
              <div className="mb-3">
                <Form.Label>性別</Form.Label>
                <Form.Select
                  value={gender}
                  onChange={(event) => {
                    setGender((event.target as any).value);
                  }}
                >
                  <option value="male">男性</option>
                  <option value="female">女性</option>
                  <option value="secret">非公開</option>
                </Form.Select>
              </div>

              {/* Age */}
              <div className="mb-3">
                <Form.Label>年齢</Form.Label>
                <Form.Control
                  placeholder="年齢を入力します"
                  value={age ?? ''}
                  onChange={(event) => {
                    setAge(+event.target.value);
                  }}
                />
                <Form.Text className="ml-auto">12歳から80歳</Form.Text>
              </div>

              {/* Email */}
              <div className="mb-3">
                <Form.Label>メールアドレス</Form.Label>
                <Form.Control
                  placeholder="メールアドレスを入力します"
                  value={email || ''}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
              </div>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div
          className="btn btn-primary"
          onClick={handleSave}
          style={{ pointerEvents: loading ? 'none' : 'auto' }}
        >
          {loading ? <Spinner animation="border" size="sm" /> : '保存する'}
        </div>
        <div
          className="btn btn-outline-dark"
          onClick={() => {
            dispatch(toggleEditModal(false));
          }}
        >
          閉じる
        </div>
      </Modal.Footer>
    </Modal>
  );
};
